import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImagePlus, FileLock2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { api, errMsg, FORMATS, SOFTWARE } from "@/lib/api";

const SRC_ACCEPT = ".cdr,.psd,.ai,.pdf,.eps,.svg,.zip,.indd,.xd,.fig";

const Toggle = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((x) => {
      const on = value.includes(x);
      return (
        <button type="button" key={x} data-testid={`${prefix}-${x}`} onClick={() => onChange(on ? value.filter((v) => v !== x) : [...value, x])}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium ${on ? "border-primary bg-primary text-white" : "border-border hover:border-primary"}`}>{x === "CANVA" ? "Canva" : x}</button>
      );
    })}
  </div>
);

const Block = ({ title, children }) => <div className="card-surface space-y-4 p-5 sm:p-6"><p className="font-semibold">{title}</p>{children}</div>;

export default function UploadTemplate() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ title: "", description: "", category_slug: "", price: "", dimensions: "", tags: "" });
  const [software, setSoftware] = useState(["CorelDRAW"]);
  const [formats, setFormats] = useState(["CDR", "JPG"]);
  const [previews, setPreviews] = useState([]);
  const [source, setSource] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const addPreviews = (files) => {
    const ok = [...files].filter((x) => /\.(jpe?g|png|webp)$/i.test(x.name) && x.size <= 10 * 1024 * 1024);
    if (ok.length < files.length) toast.error("Previews must be JPG/PNG/WEBP under 10MB");
    setPreviews((p) => [...p, ...ok].slice(0, 6));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category_slug) return toast.error("Choose a category");
    if (f.description.trim().length < 20) return toast.error("Description must be at least 20 characters");
    if (!previews.length) return toast.error("Add at least one preview image");
    if (!source) return toast.error("Attach the original editable file");
    if (!agree) return toast.error("Please confirm you own the rights to this design");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("software", software.join(","));
    fd.append("file_formats", formats.join(","));
    fd.append("copyright_confirmed", "true");
    previews.forEach((p) => fd.append("previews", p));
    fd.append("source_file", source);
    setBusy(true);
    try {
      const { data } = await api.post("/seller/templates", fd, { timeout: 600000 });
      toast.success(data.status === "approved" ? "Template published!" : "Uploaded! It will go live after review.");
      nav("/seller/templates");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit}>
      <PageHeader eyebrow="Sell a template" title="Upload Template" />
      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          <Block title="Details">
            <div className="space-y-2"><Label>Template title</Label><Input data-testid="upload-title-input" required minLength={3} maxLength={120} value={f.title} onChange={set("title")} placeholder="Professional Birthday Flyer" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea data-testid="upload-description-input" required rows={5} value={f.description} onChange={set("description")} placeholder="What's included, fonts used, how to edit…" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={f.category_slug} onValueChange={(v) => setF({ ...f, category_slug: v })}>
                  <SelectTrigger data-testid="upload-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug} data-testid={`upload-category-${c.slug}`}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Price (₦)</Label><Input data-testid="upload-price-input" type="number" min={100} required value={f.price} onChange={set("price")} placeholder="3000" /></div>
              <div className="space-y-2"><Label>Dimensions</Label><Input data-testid="upload-dimensions-input" value={f.dimensions} onChange={set("dimensions")} placeholder="1080 × 1350 px" /></div>
              <div className="space-y-2"><Label>Tags (comma separated)</Label><Input data-testid="upload-tags-input" value={f.tags} onChange={set("tags")} placeholder="birthday, flyer, gold" /></div>
            </div>
          </Block>
          <Block title="Software & file format">
            <div><p className="mb-2 text-sm text-muted-foreground">Software</p><Toggle items={SOFTWARE} value={software} onChange={setSoftware} prefix="upload-software" /></div>
            <div><p className="mb-2 text-sm text-muted-foreground">File formats included</p><Toggle items={FORMATS} value={formats} onChange={setFormats} prefix="upload-format" /></div>
          </Block>
        </div>
        <div className="space-y-5">
          <Block title="Preview images (public)">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border p-6 text-center text-sm text-muted-foreground hover:border-primary">
              <ImagePlus className="text-primary" />Add JPG/PNG previews (max 6, 10MB each)
              <input data-testid="upload-previews-input" type="file" accept=".jpg,.jpeg,.png,.webp" multiple hidden onChange={(e) => addPreviews(e.target.files)} />
            </label>
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {previews.map((p, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg">
                    <img src={URL.createObjectURL(p)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setPreviews(previews.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </Block>
          <Block title="Original editable file (private)">
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border p-6 text-center text-sm text-muted-foreground hover:border-primary">
              <FileLock2 className="text-primary" />{source ? <span className="font-medium text-foreground" data-testid="upload-source-name">{source.name}</span> : "Attach .CDR, .PSD, .AI, .PDF, .EPS, .SVG or .ZIP"}
              <input data-testid="upload-source-input" type="file" accept={SRC_ACCEPT} hidden onChange={(e) => setSource(e.target.files[0] || null)} />
            </label>
            <p className="text-xs text-muted-foreground">Stored in private storage. Delivered unchanged to buyers only after verified payment.</p>
          </Block>
          <div className="card-surface flex gap-3 p-5">
            <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="upload-copyright-checkbox" />
            <label htmlFor="agree" className="text-sm leading-snug">I confirm that I own this design or have the necessary rights to sell this template.</label>
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="upload-submit-btn">{busy ? <><Loader2 className="mr-2 animate-spin" />Uploading securely…</> : "Submit template"}</Button>
        </div>
      </div>
    </form>
  );
}
